import React, { useState } from 'react';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaFileUpload, FaBriefcase, FaShieldAlt, FaUsers, FaClock } from 'react-icons/fa';
import api from '../services/api';
import './Careers.css';

function Careers() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    position: '',
    experience: '',
    coverLetter: ''
  });
  const [cvFile, setCvFile] = useState(null);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setCvFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    // Encode form data for Netlify
    const encode = (data) => {
      return Object.keys(data)
        .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
        .join('&');
    };

    try {
      // For Netlify Forms, we'll submit the form data
      // Note: File uploads need special handling with Netlify Forms
      const formDataToSend = {
        'form-name': 'careers',
        ...formData,
        cvFileName: cvFile ? cvFile.name : ''
      };

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode(formDataToSend)
      });

      if (response.ok) {
        // If file exists, submit it separately via API or handle differently
        if (cvFile) {
          try {
            const fileFormData = new FormData();
            fileFormData.append('cv', cvFile);
            Object.keys(formData).forEach(key => {
              fileFormData.append(key, formData[key]);
            });
            await api.post('/api/careers', fileFormData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (fileError) {
            console.log('File upload failed, but form submitted to Netlify');
          }
        }

        setStatus({ 
          type: 'success', 
          message: 'Thank you for your application! We will review your details and get back to you soon.' 
        });
        
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          address: '',
          position: '',
          experience: '',
          coverLetter: ''
        });
        setCvFile(null);
        document.getElementById('cv-upload').value = '';
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      // Fallback to API if Netlify Forms fails
      try {
        const formDataToSend = new FormData();
        Object.keys(formData).forEach(key => {
          formDataToSend.append(key, formData[key]);
        });
        if (cvFile) {
          formDataToSend.append('cv', cvFile);
        }
        await api.post('/api/careers', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setStatus({ 
          type: 'success', 
          message: 'Thank you for your application! We will review your details and get back to you soon.' 
        });
        setFormData({
          fullName: '', email: '', phone: '', address: '',
          position: '', experience: '', coverLetter: ''
        });
        setCvFile(null);
        document.getElementById('cv-upload').value = '';
      } catch (apiError) {
        setStatus({ 
          type: 'error', 
          message: 'Something went wrong. Please try again later or contact us directly.' 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="careers-page">
      {/* Hero Section */}
      <section className="careers-hero">
        <div className="container">
          <h1 className="careers-title">Join Our Team</h1>
          <p className="careers-subtitle">
            V&R Courier Ltd is always looking for dedicated professionals to join our growing team. 
            Explore exciting career opportunities in logistics and delivery services.
          </p>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="why-work-section">
        <div className="container">
          <h2 className="section-title">Why Work With V&R Courier Ltd?</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <FaBriefcase className="benefit-icon" />
              <h3>Career Growth</h3>
              <p>Opportunities for professional development and advancement</p>
            </div>
            <div className="benefit-card">
              <FaClock className="benefit-icon" />
              <h3>Flexible Schedules</h3>
              <p>Work-life balance with flexible working arrangements</p>
            </div>
            <div className="benefit-card">
              <FaShieldAlt className="benefit-icon" />
              <h3>Competitive Packages</h3>
              <p>Attractive salary and benefits packages</p>
            </div>
            <div className="benefit-card">
              <FaUsers className="benefit-icon" />
              <h3>Great Team</h3>
              <p>Work with a supportive and professional team</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="application-section">
        <div className="container">
          <h2 className="section-title">Submit Your Application</h2>
          <p className="section-subtitle">
            Fill out the form below and attach your CV. We'll review your application and contact you if there's a suitable position.
          </p>

          <form 
            name="careers" 
            method="POST" 
            data-netlify="true" 
            netlify-honeypot="bot-field"
            encType="multipart/form-data"
            className="careers-form" 
            onSubmit={handleSubmit}
          >
            <input type="hidden" name="form-name" value="careers" />
            <input type="hidden" name="bot-field" />
            {status.message && (
              <div className={`form-status ${status.type}`}>
                {status.message}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>
                  <FaUser className="input-icon" />
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>
                  <FaEnvelope className="input-icon" />
                  Email Address <span className="required">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <FaPhone className="input-icon" />
                  Phone Number <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+44 7502 109790"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>
                  <FaMapMarkerAlt className="input-icon" />
                  Address <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Your full address"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  <FaBriefcase className="input-icon" />
                  Position Applied For
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  placeholder="e.g., Delivery Driver, Warehouse Operative"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>
                  Years of Experience
                </label>
                <input
                  type="text"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  placeholder="e.g., 2 years in logistics"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label>
                <FaFileUpload className="input-icon" />
                Upload CV/Resume <span className="required">*</span>
              </label>
              <div className="file-upload-wrapper">
                <input
                  type="file"
                  id="cv-upload"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  required
                  disabled={loading}
                  className="file-input"
                />
                <label htmlFor="cv-upload" className="file-label">
                  {cvFile ? cvFile.name : 'Choose file (PDF, DOC, DOCX)'}
                </label>
              </div>
              <small className="file-help">Maximum file size: 5MB</small>
            </div>

            <div className="form-group">
              <label>
                Cover Letter / Additional Information
              </label>
              <textarea
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleChange}
                placeholder="Tell us why you'd like to join our team..."
                rows="6"
                disabled={loading}
              ></textarea>
            </div>

            <button type="submit" className="btn btn-primary btn-submit" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

export default Careers;

